import { useEffect } from "react";

interface SeoProps {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  jsonLd?: object[];
}

const BASE_URL = "https://comicdex.github.io";

/**
 * 统一的 SEO 管理组件：更新页面标题、meta 描述、关键词、canonical 与 JSON-LD 结构化数据。
 */
export default function Seo({ title, description, keywords, canonical, jsonLd }: SeoProps) {
  useEffect(() => {
    document.title = title;

    const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertMeta("property", "og:title", title);
    upsertMeta("name", "twitter:title", title);
    if (canonical) {
      const fullUrl = canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`;
      let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = fullUrl;
      upsertMeta("property", "og:url", fullUrl);
    }

    // 注入 JSON-LD 结构化数据
    const prevBlocks = document.head.querySelectorAll("script[data-seo-jsonld]");
    prevBlocks.forEach((node) => node.remove());
    if (jsonLd && jsonLd.length) {
      jsonLd.forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-jsonld", "true");
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, canonical, jsonLd]);

  return null;
}

export { BASE_URL };